import React, { PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { ModalFadeIn, ScaleIn } from './Animations'
import { IconButton } from './Button'
import Close from '../icons/Close'
import NavbarController from '../state/NavbarController'

const DrawerSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(28px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`

const ModalContainer = styled.div<{ $drawer?: boolean }>`
  background-color: rgba(7, 7, 40, 0.75);
  backdrop-filter: blur(2px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 102;
  display: flex;
  align-items: ${({ $drawer }) => $drawer ? 'stretch' : 'center'};
  justify-content: ${({ $drawer }) => $drawer ? 'flex-end' : 'center'};
  animation: ${ModalFadeIn} 0.2s;
  padding: ${({ $drawer }) => $drawer ? '12px' : '16px'};
`

const ModalInner = styled.div<{ minHeight?: string, $drawer?: boolean }>`
  background-color: var(--surface, #ffffff);
  border: 1px solid var(--border-strong, rgba(226, 232, 240, 0.95));
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.12);
  min-width: min(450px, calc(100vw - 32px));
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
  max-width: calc(100vw - 32px);
  border-radius: ${({ $drawer }) => $drawer ? '18px' : '22px'};
  animation: ${({ $drawer }) => $drawer ? DrawerSlideIn : ScaleIn} 0.2s ease-out;
  max-height: calc(100vh - ${({ $drawer }) => $drawer ? '24px' : '32px'});
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: contain;

  ${({ $drawer }) => $drawer && `
    height: 100%;
    min-width: 0;
    width: min(480px, calc(100vw - 24px));
  `}

  > * {
    box-sizing: border-box;
    max-width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 520px) {
    min-width: 0;
    width: 100%;
  }
`

const ModalBody = styled.div<{ $hasHeader: boolean, $hasFooter: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 12px;
  min-height: 0;
  overflow: auto;
  padding: ${({ $hasHeader, $hasFooter }) => {
    const top = $hasHeader ? '16px' : '18px'
    const bottom = $hasFooter ? '12px' : ($hasHeader ? '18px' : '20px')
    return `${top} 20px ${bottom}`
  }};
`

export const ModalFooter = styled.div<{ $fullWidthAction?: boolean, $embedded?: boolean }>`
  display: flex;
  justify-content: ${({ $fullWidthAction }) => $fullWidthAction ? 'stretch' : 'flex-end'};
  gap: 10px;
  padding: ${({ $embedded }) => $embedded ? '14px 0 0' : '14px 20px 16px'};
  border-top: 1px solid var(--hairline, #eef1f5);
  background-color: ${({ $embedded }) => $embedded ? 'transparent' : 'var(--surface, #ffffff)'};

  > * {
    ${({ $fullWidthAction }) => $fullWidthAction ? 'flex: 1 1 auto;' : ''}
  }
`

export type ModalProps = PropsWithChildren<{
  visible: boolean
  onClose: any
  innerStyles?: React.CSSProperties
  title?: string
  minHeight?: string
  uncloseable?: boolean
  /**
   * 'drawer' presents the same content as a full-height sheet on the right —
   * the house style for flows that sit beside the page rather than on top of
   * it. Defaults to the centered dialog.
   */
  presentation?: 'dialog' | 'drawer'
}>

const Modal = (props: ModalProps): ReactElement => {
  const inner = useRef<HTMLDivElement>(null)

  const [isMouseDown, setIsMouseDown] = useState(false)

  useEffect(() => {
    NavbarController.setModalOpen(props.visible)
    return () => NavbarController.setModalOpen(false)
  }, [props.visible])

  // Escape closes the modal. Open dropdowns swallow Escape in the capture
  // phase, so this bubble-phase listener only fires once they are closed.
  useEffect(() => {
    if (!props.visible) {
      return
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        props.onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [props.visible, props.onClose])

  if (!props.visible) {
    return <></>
  }

  const childrenArray = React.Children.toArray(props.children)
  const footerIndex = childrenArray.findIndex(
    child => React.isValidElement(child) && (
      child.type === ModalFooter ||
      (child.type as any).name === 'ModalFooter' ||
      (child.type as any).displayName === 'ModalFooter'
    )
  )
  let footer: React.ReactNode = null
  let bodyChildren = props.children

  if (footerIndex !== -1) {
    footer = childrenArray[footerIndex]
    bodyChildren = childrenArray.filter((_, idx) => idx !== footerIndex)
  }

  const isDrawer = props.presentation === 'drawer'

  return <ModalContainer
    $drawer={isDrawer}
    onMouseDown={e => {
      if (!(e.target === inner.current || inner.current?.contains(e.target as any))) {
        setIsMouseDown(true)
      }
    }}
    onMouseUp={e => {
      if (!(e.target === inner.current || inner.current?.contains(e.target as any)) && isMouseDown) {
        if (!props.uncloseable) {
          props.onClose()
        }
      }
      setIsMouseDown(false)
    }}>
    <ModalInner
      minHeight={props.minHeight}
      $drawer={isDrawer}
      ref={inner}
      style={props.innerStyles}>

      {
        props.title &&
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          padding: '16px 20px 14px',
          alignItems: 'center',
          borderBottom: '1px solid var(--hairline, #eef1f5)',
          backgroundColor: 'var(--surface, #ffffff)'
        }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink, #161c27)', lineHeight: 1.2 }}>{props.title}</span>
          {
            !props.uncloseable &&
            <IconButton type='button' onClick={() => props.onClose()} style={{ padding: 5, borderRadius: 10 }}>
              <Close style={{ width: '15px' }} />
            </IconButton>
          }
        </div>
      }

      <ModalBody $hasHeader={!!props.title} $hasFooter={!!footer}>
        {bodyChildren}
      </ModalBody>

      {footer}
    </ModalInner>
  </ModalContainer>
}

export default Modal
